/**
 * VcardService — vCard-Endpunkte über die http-Instanz (Spec §7.4).
 * Die http-Instanz entpackt `response.data` bereits im Interceptor, daher
 * geben die Methoden die Nutzdaten direkt zurück.
 */

import http from './http'
import type { Vcard, VcardInput } from '../types'

/**
 * Listet vCards, optional gefiltert nach Brand.
 *
 * @param brandKey Optionaler Brand-Filter (Query-Param)
 * @returns Liste der (für die Rolle sichtbaren) vCards
 */
async function list(brandKey?: string): Promise<Vcard[]> {
  const params = brandKey ? { brandKey } : {}
  return http.get('/vcards', { params })
}

/**
 * Holt eine einzelne vCard über ihren urlKey.
 *
 * @param urlKey Öffentlicher Schlüssel der vCard
 * @returns Die vCard
 */
async function get(urlKey: string): Promise<Vcard> {
  return http.get(`/vcards/${urlKey}`)
}

/**
 * Legt eine neue vCard an (nur Admin; Nutzerkonto entsteht serverseitig).
 *
 * @param payload Editier-Payload der neuen vCard
 * @returns Die angelegte vCard (inkl. generiertem urlKey)
 */
async function create(payload: VcardInput): Promise<Vcard> {
  return http.post('/vcards', payload)
}

/**
 * Ersetzt eine bestehende vCard (PUT-Replace).
 *
 * @param urlKey Schlüssel der zu ändernden vCard
 * @param payload Vollständiger Editier-Payload
 * @returns Die aktualisierte vCard
 */
async function update(urlKey: string, payload: VcardInput): Promise<Vcard> {
  return http.put(`/vcards/${urlKey}`, payload)
}

/**
 * Schaltet die Sichtbarkeit einer vCard um.
 *
 * @param urlKey Schlüssel der vCard
 * @param active Neuer Aktiv-Status
 * @returns Die aktualisierte vCard
 */
async function setActive(urlKey: string, active: boolean): Promise<Vcard> {
  return http.put(`/vcards/${urlKey}/active`, { active })
}

/**
 * Löscht eine vCard (nur Admin).
 *
 * @param urlKey Schlüssel der zu löschenden vCard
 */
async function remove(urlKey: string): Promise<void> {
  await http.delete(`/vcards/${urlKey}`)
}

/**
 * Holt die eigene vCard des eingeloggten Nutzers.
 *
 * @returns Die eigene vCard
 */
async function getMine(): Promise<Vcard> {
  return http.get('/me/vcard')
}

/**
 * Speichert die eigene vCard des eingeloggten Nutzers.
 *
 * @param payload Editier-Payload der eigenen vCard
 * @returns Die aktualisierte vCard
 */
async function updateMine(payload: VcardInput): Promise<Vcard> {
  return http.put('/me/vcard', payload)
}

export const VcardService = {
  list,
  get,
  create,
  update,
  setActive,
  remove,
  getMine,
  updateMine,
}
